// src/pages/DataAccessRequest.js
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Database, Send, Save, ArrowLeft, CheckCircle,
  User, Globe, FileText, Layers, AlertCircle
} from "lucide-react";

const DATASETS = [
  { id: "tec", label: "Total Electron Content (TEC)", desc: "GNSS-derived ionospheric TEC maps" },
  { id: "scintillation", label: "Scintillation Indices (S4, σφ)", desc: "Amplitude & phase scintillation" },
  { id: "rinex", label: "RINEX Observation Files", desc: "Raw GNSS observation data" },
  { id: "gic", label: "Geomagnetic / GIC Data", desc: "Ground magnetic field measurements" },
  { id: "weather", label: "Terrestrial Weather Forecasts", desc: "GFS-based forecast outputs" },
  { id: "model", label: "Model Prediction Outputs", desc: "Deep learning TEC/scintillation predictions" },
];

const PURPOSE_OPTIONS = [
  "Academic / PhD Research",
  "MSc / Undergraduate Research",
  "Operational / Commercial Use",
  "Government / Policy Use",
  "Aviation Safety",
  "Power Grid / Infrastructure",
  "Software Development",
  "Other",
];

const DURATION_OPTIONS = [
  "One-time download",
  "1–3 months",
  "3–6 months",
  "6–12 months",
  "Ongoing / Long-term",
];

const EMPTY_FORM = {
  fullName: "",
  email: "",
  institution: "",
  country: "",
  role: "",
  purpose: "",
  purposeDetail: "",
  datasets: [],
  duration: "",
  dataFormat: "",
  additionalInfo: "",
  agreeTerms: false,
};

export default function DataAccessRequest() {
  const [form, setForm] = useState(() => {
    const saved = localStorage.getItem("dataAccessDraft");
    return saved ? JSON.parse(saved) : EMPTY_FORM;
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  }

  function handleDatasetToggle(id) {
    setForm((prev) => ({
      ...prev,
      datasets: prev.datasets.includes(id)
        ? prev.datasets.filter((d) => d !== id)
        : [...prev.datasets, id],
    }));
    if (errors.datasets) setErrors((prev) => ({ ...prev, datasets: null }));
  }

  function validate() {
    const e = {};
    if (!form.fullName.trim())       e.fullName = "Full name is required";
    if (!form.email.trim())          e.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = "Enter a valid email";
    if (!form.institution.trim())    e.institution = "Institution is required";
    if (!form.country.trim())        e.country = "Country is required";
    if (!form.purpose)               e.purpose = "Please select a purpose";
    if (form.datasets.length === 0)  e.datasets = "Select at least one dataset";
    if (!form.duration)              e.duration = "Please select a duration";
    if (!form.agreeTerms)            e.agreeTerms = "You must agree to the terms";
    return e;
  }

  function handleSaveDraft() {
    localStorage.setItem("dataAccessDraft", JSON.stringify(form));
    setSaving(true);
    setTimeout(() => setSaving(false), 2000);
  }

  function buildMailtoBody() {
    const datasetNames = form.datasets
      .map((id) => DATASETS.find((d) => d.id === id)?.label)
      .join(", ");

    return encodeURIComponent(
`Data Access Request Form
========================

APPLICANT DETAILS
-----------------
Full Name:        ${form.fullName}
Email:            ${form.email}
Institution:      ${form.institution}
Country:          ${form.country}
Role / Position:  ${form.role || "Not specified"}

REQUEST DETAILS
---------------
Purpose:          ${form.purpose}
Details:          ${form.purposeDetail || "Not specified"}
Datasets needed:  ${datasetNames || "Not specified"}
Access duration:  ${form.duration}
Preferred format: ${form.dataFormat || "Any"}

ADDITIONAL INFORMATION
----------------------
${form.additionalInfo || "None provided"}

---
Submitted via Busitema Space Science Centre website.`
    );
  }

  function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      const firstErr = document.querySelector(".field-error");
      firstErr?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    const subject = encodeURIComponent("Data Access Request – GCS Portal");
    const body = buildMailtoBody();
    window.location.href = `mailto:space.sci@busitema.ac.ug?subject=${subject}&body=${body}`;
    localStorage.removeItem("dataAccessDraft");
    setSubmitted(true);
  }

  if (submitted) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4"
        style={{ background: "var(--space-navy)" }}
      >
        <div className="text-center max-w-lg">
          <div
            className="mx-auto mb-6 w-20 h-20 rounded-full flex items-center justify-center"
            style={{ background: "rgba(0,188,212,0.15)", border: "2px solid var(--space-teal)" }}
          >
            <CheckCircle size={40} style={{ color: "var(--space-teal)" }} />
          </div>
          <h1 className="text-white text-3xl font-black mb-3">Request Submitted!</h1>
          <p className="text-gray-400 leading-relaxed mb-2">
            Your email client should have opened with a pre-filled message to{" "}
            <span style={{ color: "var(--space-teal)" }}>space.sci@busitema.ac.ug</span>.
          </p>
          <p className="text-gray-500 text-sm mb-8">
            If it didn't open automatically, please email us directly with your request details.
            We typically respond within 2–3 business days.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/" className="btn-primary px-6 py-2.5">
              <ArrowLeft size={16} /> Back to Home
            </Link>
            <button
              onClick={() => { setSubmitted(false); setForm(EMPTY_FORM); }}
              className="btn-secondary px-6 py-2.5"
            >
              Submit Another Request
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "var(--space-navy)", minHeight: "100vh" }}>

      {/* Page Header */}
      <div
        style={{
          background: "var(--space-navy-mid)",
          borderBottom: "1px solid rgba(255,255,255,0.07)",
          paddingTop: "2.5rem",
          paddingBottom: "2.5rem",
        }}
      >
        <div className="section-container">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm mb-5"
            style={{ color: "var(--space-teal)", textDecoration: "none" }}
          >
            <ArrowLeft size={15} /> Back to Home
          </Link>
          <div className="flex items-start gap-4">
            <div
              className="p-3 rounded-xl shrink-0"
              style={{ background: "rgba(0,188,212,0.12)", border: "1px solid rgba(0,188,212,0.25)" }}
            >
              <Database size={28} style={{ color: "var(--space-teal)" }} />
            </div>
            <div>
              <h1 className="text-white text-2xl sm:text-3xl font-black mb-1">
                Data Access Request
              </h1>
              <p className="text-gray-400 text-sm sm:text-base">
                Complete this form to request access to the Busitema Space Science Center datasets.
                Fields marked <span style={{ color: "#ef5350" }}>*</span> are required.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} noValidate>
        <div className="section-container py-10" style={{ maxWidth: "800px" }}>

          {/* ── SECTION 1: Applicant Details ── */}
          <FormSection icon={User} title="Applicant Details">

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field
                label="Full Name" name="fullName" required
                placeholder="e.g. Dr. Jane Nakato"
                value={form.fullName} onChange={handleChange}
                error={errors.fullName}
              />
              <Field
                label="Email Address" name="email" type="email" required
                placeholder="you@institution.ac.ug"
                value={form.email} onChange={handleChange}
                error={errors.email}
              />
              <Field
                label="Institution / Organisation" name="institution" required
                placeholder="e.g. Makerere University"
                value={form.institution} onChange={handleChange}
                error={errors.institution}
              />
              <Field
                label="Country" name="country" required
                placeholder="e.g. Uganda"
                value={form.country} onChange={handleChange}
                error={errors.country}
              />
              <Field
                label="Role / Position"
                name="role"
                placeholder="e.g. PhD Student, Research Officer"
                value={form.role} onChange={handleChange}
                className="sm:col-span-2"
              />
            </div>

          </FormSection>

          {/* ── SECTION 2: Request Details ── */}
          <FormSection icon={FileText} title="Request Details">

            {/* Purpose dropdown */}
            <div className="mb-5">
              <label className="form-label">
                Purpose of Data Use <span style={{ color: "#ef5350" }}>*</span>
              </label>
              <select
                name="purpose"
                value={form.purpose}
                onChange={handleChange}
                className={`form-input w-full ${errors.purpose ? "field-error" : ""}`}
                style={errors.purpose ? { borderColor: "#ef5350" } : {}}
              >
                <option value="">— Select a purpose —</option>
                {PURPOSE_OPTIONS.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
              {errors.purpose && <ErrorMsg msg={errors.purpose} />}
            </div>

            {/* Purpose detail */}
            <div className="mb-5">
              <label className="form-label">Describe Your Research / Project</label>
              <textarea
                name="purposeDetail"
                rows={3}
                value={form.purposeDetail}
                onChange={handleChange}
                placeholder="Briefly describe what you are working on and how you plan to use the data…"
                className="form-input w-full"
                style={{ resize: "vertical" }}
              />
            </div>

            {/* Access duration */}
            <div className="mb-1">
              <label className="form-label">
                Access Duration Needed <span style={{ color: "#ef5350" }}>*</span>
              </label>
              <div className="flex flex-wrap gap-2 mt-1">
                {DURATION_OPTIONS.map((d) => (
                  <button
                    key={d} type="button"
                    onClick={() => {
                      setForm((p) => ({ ...p, duration: d }));
                      if (errors.duration) setErrors((p) => ({ ...p, duration: null }));
                    }}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium"
                    style={{
                      background: form.duration === d ? "var(--space-teal)" : "rgba(255,255,255,0.06)",
                      color: form.duration === d ? "#071220" : "rgba(255,255,255,0.7)",
                      border: form.duration === d ? "1px solid var(--space-teal)" : "1px solid rgba(255,255,255,0.12)",
                      cursor: "pointer",
                      transition: "all 0.15s",
                    }}
                  >
                    {d}
                  </button>
                ))}
              </div>
              {errors.duration && <ErrorMsg msg={errors.duration} />}
            </div>

          </FormSection>

          {/* ── SECTION 3: Datasets ── */}
          <FormSection icon={Layers} title="Datasets Required">
            <p className="text-gray-500 text-sm mb-4">
              Select all datasets you need. <span style={{ color: "#ef5350" }}>*</span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {DATASETS.map((ds) => {
                const checked = form.datasets.includes(ds.id);
                return (
                  <button
                    key={ds.id}
                    type="button"
                    onClick={() => handleDatasetToggle(ds.id)}
                    className="text-left rounded-xl p-4"
                    style={{
                      background: checked ? "rgba(0,188,212,0.1)" : "rgba(255,255,255,0.04)",
                      border: checked ? "1px solid rgba(0,188,212,0.5)" : "1px solid rgba(255,255,255,0.08)",
                      cursor: "pointer",
                      transition: "all 0.15s",
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="mt-0.5 w-4 h-4 rounded shrink-0 flex items-center justify-center"
                        style={{
                          background: checked ? "var(--space-teal)" : "transparent",
                          border: checked ? "1px solid var(--space-teal)" : "1px solid rgba(255,255,255,0.3)",
                        }}
                      >
                        {checked && (
                          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                            <path d="M1 4l3 3 5-6" stroke="#071220" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                      <div>
                        <div className="text-sm font-semibold" style={{ color: checked ? "var(--space-teal)" : "rgba(255,255,255,0.85)" }}>
                          {ds.label}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">{ds.desc}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            {errors.datasets && <ErrorMsg msg={errors.datasets} />}

            {/* Preferred format */}
            <div className="mt-5">
              <label className="form-label">Preferred Data Format</label>
              <Field
                name="dataFormat"
                placeholder="e.g. CSV, JSON, RINEX, NetCDF — or leave blank for any"
                value={form.dataFormat} onChange={handleChange}
              />
            </div>
          </FormSection>

          {/* ── SECTION 4: Additional Info ── */}
          <FormSection icon={Globe} title="Additional Information">
            <div>
              <label className="form-label">Any other details, special requirements, or questions?</label>
              <textarea
                name="additionalInfo"
                rows={4}
                value={form.additionalInfo}
                onChange={handleChange}
                placeholder="e.g. specific date ranges, geographic coverage, collaborating institutions…"
                className="form-input w-full"
                style={{ resize: "vertical" }}
              />
            </div>
          </FormSection>

          {/* ── Terms ── */}
          <div
            className="rounded-xl p-5 mb-8"
            style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
          >
            <label className="flex items-start gap-3 cursor-pointer">
              <div
                className="mt-0.5 w-5 h-5 rounded shrink-0 flex items-center justify-center"
                style={{
                  background: form.agreeTerms ? "var(--busitema-green)" : "transparent",
                  border: form.agreeTerms
                    ? "1px solid var(--busitema-green)"
                    : errors.agreeTerms
                    ? "1px solid #ef5350"
                    : "1px solid rgba(255,255,255,0.3)",
                }}
                onClick={() => {
                  setForm((p) => ({ ...p, agreeTerms: !p.agreeTerms }));
                  if (errors.agreeTerms) setErrors((p) => ({ ...p, agreeTerms: null }));
                }}
              >
                {form.agreeTerms && (
                  <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
                    <path d="M1 4.5l3.5 3.5 6-7" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </div>
              <span className="text-sm text-gray-400 leading-relaxed">
                I confirm that the information provided is accurate, and I agree to use the data
                solely for the stated purpose. I understand that data is provided for
                non-commercial research use unless otherwise agreed in writing with Busitema
                Space Science Centre. <span style={{ color: "#ef5350" }}>*</span>
              </span>
            </label>
            {errors.agreeTerms && <ErrorMsg msg={errors.agreeTerms} />}
          </div>

          {/* ── Action buttons ── */}
          <div className="flex flex-wrap gap-3 items-center">
            <button
              type="submit"
              className="btn-primary px-8 py-3 text-base"
              style={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              <Send size={17} />
              Submit Request
            </button>

            <button
              type="button"
              onClick={handleSaveDraft}
              className="px-6 py-3 rounded-xl text-sm font-semibold"
              style={{
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.14)",
                color: saving ? "var(--space-teal)" : "rgba(255,255,255,0.7)",
                cursor: "pointer",
                display: "flex", alignItems: "center", gap: "7px",
                transition: "color 0.2s",
              }}
            >
              <Save size={16} />
              {saving ? "Draft Saved!" : "Save Draft"}
            </button>

            <Link
              to="/"
              className="px-6 py-3 rounded-xl text-sm font-semibold"
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "rgba(255,255,255,0.45)",
                textDecoration: "none",
                display: "flex", alignItems: "center", gap: "7px",
              }}
            >
              <ArrowLeft size={15} /> Cancel
            </Link>
          </div>

          <p className="text-gray-600 text-xs mt-4">
            Clicking "Submit Request" will open your email client with a pre-filled message to{" "}
            <span style={{ color: "var(--space-teal)" }}>space.sci@busitema.ac.ug</span>.
            Draft data is saved locally in your browser only.
          </p>

        </div>
      </form>
    </div>
  );
}

/* ── Reusable sub-components ── */

function FormSection({ icon: Icon, title, children }) {
  return (
    <div
      className="rounded-2xl p-6 mb-6"
      style={{ background: "var(--space-navy-mid)", border: "1px solid rgba(255,255,255,0.07)" }}
    >
      <div className="flex items-center gap-2.5 mb-5">
        <Icon size={18} style={{ color: "var(--space-teal)" }} />
        <h2 className="text-white font-bold text-base">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function Field({ label, name, type = "text", placeholder, value, onChange, error, required, className = "" }) {
  return (
    <div className={className}>
      {label && (
        <label className="form-label">
          {label} {required && <span style={{ color: "#ef5350" }}>*</span>}
        </label>
      )}
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`form-input w-full ${error ? "field-error" : ""}`}
        style={error ? { borderColor: "#ef5350" } : {}}
      />
      {error && <ErrorMsg msg={error} />}
    </div>
  );
}

function ErrorMsg({ msg }) {
  return (
    <div className="flex items-center gap-1.5 mt-1.5">
      <AlertCircle size={13} style={{ color: "#ef5350", flexShrink: 0 }} />
      <span className="text-xs" style={{ color: "#ef5350" }}>{msg}</span>
    </div>
  );
}
